import {
  Box,
  colors,
  DATE_FORMAT,
  Icon,
  PanelSection,
} from '@dev-spendesk/grapes';
import { addDays } from 'date-fns';
import { format, fromNumber, toNumber } from 'ezmoney';

import { useTranslation } from 'common/hooks/useTranslation';

import { VerticalLine } from '../../../requests/components/common/VerticalLine';
import {
  type AllowanceLocation,
  isCountryWithAdjustedRates,
} from '../../allowanceLocation';
import {
  findNextArrival,
  getLastArrival,
  hasChangedLocation,
  isForeignAllowanceCanTakePrecedence,
  type PerDiem,
  type PerDiemDayStay,
  type PerDiemTripDay,
} from '../../perDiem';
import {
  getCountryName,
  type Location,
  toLocaleString,
} from '../../perDiemLocation';

import './PerDiemDaysBreakdown.css';

type Destination = {
  destination: string;
  subtotal: number;
  days: {
    date: string;
    mealString: string;
    dailyAmount: string;
  }[];
  currency: string;
  destinationNumber: number;
};

type Props = {
  perDiem: PerDiem;
  userLang: string;
  allowanceLocations: ReadonlyArray<AllowanceLocation>;
  companyCountry: string;
};

const getFirstDay = (request: PerDiem) => {
  return (
    request.tripDays[0].stays[0].departureTime ||
    request.tripDays[0].stays[1].departureTime
  );
};

const isOneDayTrip = (tripDays: PerDiemTripDay[]) => tripDays.length === 1;

const isLastTripDay = (tripDays: PerDiemTripDay[], index: number) =>
  index === tripDays.length - 1;

const hasMultipleDestinations = (dayStays: PerDiemDayStay[]) =>
  dayStays.length > 1;

const removeReturnDestination = (dayStays: PerDiemDayStay[]) => {
  return dayStays.slice(0, -1);
};

const findLastArrivalBeforeReturnDestination = (dayStays: PerDiemDayStay[]) =>
  getLastArrival(removeReturnDestination(dayStays));

const getLocation = (perDiemTripDays: PerDiemTripDay[], index: number) => {
  const { stays } = perDiemTripDays[index];
  if (isOneDayTrip(perDiemTripDays) && hasMultipleDestinations(stays)) {
    const stay = findLastArrivalBeforeReturnDestination(stays);
    if (stay) {
      return stay.location;
    }
  }
  const stay = getLastArrival(stays);
  if (stay) {
    return stay.location;
  }

  return findNextArrival(perDiemTripDays, index)?.location;
};

const lastMemorizedLocationIsDifferent = (
  oldLocation: Location | undefined,
  newLocation: Location | undefined,
) => {
  return (
    oldLocation?.country !== newLocation?.country ||
    oldLocation?.city !== newLocation?.city
  );
};

export const PerDiemDaysBreakdown = ({
  perDiem,
  userLang,
  allowanceLocations,
  companyCountry,
}: Props) => {
  const { t, localeFormat } = useTranslation('global');

  const getMealString = (day: PerDiemTripDay) => {
    const days = [
      !day.mealsIncluded.isBreakfastIncluded &&
        `${t('forms.perDiemAllowance.mealSelector.breakfast')}`,
      !day.mealsIncluded.isLunchIncluded &&
        `${t('forms.perDiemAllowance.mealSelector.lunch')}`,
      !day.mealsIncluded.isDinnerIncluded &&
        `${t('forms.perDiemAllowance.mealSelector.dinner')}`,
    ];
    return days.filter((d) => !!d).join(' - ');
  };

  const locationToString = (location: Location) => {
    return isCountryWithAdjustedRates(location, allowanceLocations)
      ? toLocaleString(location, userLang)
      : getCountryName(location, userLang);
  };

  const { tripDays } = perDiem;

  const { location } = tripDays[0].stays[0];

  let computedDestinationNumber = 0;
  let memorizedLastLocationForReal: Location | undefined;
  const destinationsDescriptions = tripDays.reduce(
    (destinations: Destination[], day, index, days) => {
      let computedLocation: Location | undefined;
      if (index === 0) {
        computedLocation = getLocation(days, 0);
        memorizedLastLocationForReal = computedLocation;
      }
      if (
        index > 0 &&
        hasChangedLocation(day.stays) &&
        lastMemorizedLocationIsDifferent(
          memorizedLastLocationForReal,
          getLocation(days, index),
        ) &&
        !isLastTripDay(tripDays, index) &&
        !isForeignAllowanceCanTakePrecedence(day.stays, companyCountry)
      ) {
        computedDestinationNumber += 1;
        computedLocation = getLocation(days, index);
        memorizedLastLocationForReal = computedLocation;
      }
      if (
        days[index - 1] &&
        !hasChangedLocation(day.stays) &&
        lastMemorizedLocationIsDifferent(
          memorizedLastLocationForReal,
          getLocation(days, index - 1),
        ) &&
        isForeignAllowanceCanTakePrecedence(
          days[index - 1].stays,
          companyCountry,
        )
      ) {
        computedDestinationNumber += 1;
        computedLocation = getLocation(days, index - 1);
        memorizedLastLocationForReal = computedLocation;
      }

      const firstDate = getFirstDay(perDiem) || new Date();
      const date = localeFormat(addDays(firstDate, index), DATE_FORMAT.CUSTOM, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      const mealString = getMealString(day);
      const dailyAmount = format(
        perDiem.allowance.dailyAllowances[index].finalAmount,
      );
      const currentDay = {
        date,
        mealString,
        dailyAmount,
      };

      if (!destinations[computedDestinationNumber]) {
        destinations[computedDestinationNumber] = {
          destination: computedLocation
            ? (computedLocation.description ??
              locationToString(computedLocation))
            : '',
          subtotal: 0,
          days: [],
          destinationNumber: computedDestinationNumber,
          currency:
            perDiem.allowance.dailyAllowances[index].finalAmount.currency,
        };
      }
      destinations[computedDestinationNumber].subtotal +=
        toNumber(perDiem.allowance.dailyAllowances[index].finalAmount) ?? 0;
      destinations[computedDestinationNumber].days.push(currentDay);
      return destinations;
    },
    [],
  );

  return (
    <PanelSection title={t('forms.perDiemAllowance.tripDetailsTitle')}>
      <div className="mb-16">
        <div className="text-content-secondary-bg-primary">
          {t('forms.perDiemAllowance.origin')}
        </div>
        <span className="text-content-primary title-m">
          {location.description ?? locationToString(location)}
        </span>
      </div>
      {destinationsDescriptions.map(
        ({ destination, destinationNumber, days, subtotal, currency }) => (
          <div
            className="[&:not(:last-child)]:mb-16"
            key={destination + destinationNumber}
          >
            <div className="mb-16">
              <div className="text-content-secondary-bg-primary">
                {t('forms.perDiemAllowance.destinationCount', {
                  number:
                    destinationsDescriptions.length === 1
                      ? undefined
                      : destinationNumber + 1,
                })}
              </div>
              <div className="text-content-primary title-m">{destination}</div>
            </div>
            <Box variant="secondary" className="p-16">
              {days.map(({ dailyAmount, date, mealString }, index) => {
                return (
                  <div
                    key={date}
                    className="PerDiemDaysBreakdown_DestinationDetailsBox mb-12"
                  >
                    <div className="relative mr-8 mt-[2px] flex items-center self-start">
                      <Icon
                        name="calendar"
                        color={colors.contentSecondaryBgSecondary}
                      />
                      {index !== days.length - 1 && (
                        <VerticalLine
                          className="PerDiemDaysBreakdown_DestinationDetailsBox__dashedLine text-content-disabled"
                          height={20}
                        />
                      )}
                    </div>
                    <div>
                      <div className="mb-4 text-content-primary body-m">{date}</div>
                      <div className="text-content-secondary-bg-primary body-s">
                        {mealString}
                      </div>
                    </div>
                    <div className="self-end text-right text-content-secondary-bg-primary body-s">
                      {dailyAmount}
                    </div>
                  </div>
                );
              })}
              <div className="separator my-16" />
              <div className="flex items-center text-content-primary body-m">
                <span className="mr-auto">
                  {t('forms.perDiemAllowance.subtotal')}
                </span>
                {format(fromNumber(subtotal, currency, 1))}
              </div>
            </Box>
          </div>
        ),
      )}
    </PanelSection>
  );
};
